import { AppConfig } from '../types';
import { getClientAuthHeaders } from './clientAuthHeaders';

let cachedAppConfig: AppConfig | null = null;
const cachedLocationsByRole: Record<string, any> = {};
let cachedInmobiliarias: Record<string, any> | null = null;

export function clearConfigCache(): void {
  cachedAppConfig = null;
  cachedInmobiliarias = null;
  Object.keys(cachedLocationsByRole).forEach((key) => {
    delete cachedLocationsByRole[key];
  });
}

export async function fetchAppConfigApi(forceRefresh = false): Promise<AppConfig> {
  if (!forceRefresh && cachedAppConfig) return cachedAppConfig;
  const headers = await getClientAuthHeaders();
  const res = await fetch(`/api/config/app${forceRefresh ? '?refresh=true' : ''}`, { headers });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Error fetching app config');
  cachedAppConfig = data.config;
  return data.config;
}

export async function fetchInmobiliariasConfigApi(forceRefresh = false): Promise<Record<string, any>> {
  if (!forceRefresh && cachedInmobiliarias) return cachedInmobiliarias;
  const headers = await getClientAuthHeaders();
  const res = await fetch(`/api/config/inmobiliarias${forceRefresh ? '?refresh=true' : ''}`, { headers });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Error fetching inmobiliarias config');
  cachedInmobiliarias = data.inmobiliarias;
  return data.inmobiliarias;
}

export async function fetchLocationsConfigApi(forceRefresh = false, explicitRole?: string): Promise<{
  tiposInmueble: string[];
  estrategiasComerciales: string[];
  ubicaciones: Record<string, Record<string, string[]>>;
}> {
  let roleKey = explicitRole ? String(explicitRole).trim() : 'ANON';
  if (!explicitRole) {
    try {
      const userStr = localStorage.getItem('auth_user_session');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user?.role) roleKey = String(user.role).trim();
      }
    } catch {}
  }

  if (!forceRefresh && cachedLocationsByRole[roleKey]) return cachedLocationsByRole[roleKey];
  
  const queryParts: string[] = [];
  if (roleKey && roleKey !== 'ANON') queryParts.push(`role=${encodeURIComponent(roleKey)}`);
  if (forceRefresh) queryParts.push('refresh=true');
  const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';

  const headers = await getClientAuthHeaders();
  const res = await fetch(`/api/config/locations${queryString}`, { headers });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Error fetching locations config');
  cachedLocationsByRole[roleKey] = data.locations;
  return data.locations;
}

export async function updateInmobiliariaApi(
  docId: string,
  subKey: string | null,
  fields: Record<string, any>
): Promise<void> {
  const res = await fetch('/api/config/inmobiliarias/update', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ docId, subKey, fields }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Error updating inmobiliaria');
  cachedInmobiliarias = null;
}

export async function setInmobiliariaApi(docId: string, dataObj: Record<string, any>): Promise<void> {
  const res = await fetch('/api/config/inmobiliarias/set', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ docId, data: dataObj }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Error setting inmobiliaria');
  cachedInmobiliarias = null;
}

export async function deleteInmobiliariaApi(docId: string): Promise<void> {
  const res = await fetch('/api/config/inmobiliarias/delete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ docId }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Error deleting inmobiliaria');
  cachedInmobiliarias = null;
}

export async function updateAppConfigParameterApi(
  parameterId: string,
  dataObj: Record<string, any>
): Promise<void> {
  const authHeaders = await getClientAuthHeaders();
  const res = await fetch('/api/config/app/parameter', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
    },
    body: JSON.stringify({ parameterId, data: dataObj }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Error al actualizar el parámetro de configuración');
  cachedAppConfig = null;
}


